import type { BaseAPIResponse } from '~/types/api';
import type { CurrentReports, Report } from '~/types/business';
import { pbRequest, pbRequestRaw } from '~/services/request';
import { ApiError } from '~/utils/errors';

// Report history and download links. Nothing here generates or schedules a report:
// every client company's reports arrive on the first of each month.
export const reportsService = {
    /**
     * What the reporting page shows: the current copy of each company report, the current
     * copy of each employee's own for the roster, and when the next run files them.
     *
     * The page has no controls on it, so this is the whole of what it reads.
     */
    async getCurrentReports(companyId: number, params?: { page?: number; per_page?: number; search?: string }): Promise<CurrentReports> {
        const query = new URLSearchParams();
        if (params?.page) query.set('page', String(params.page));
        if (params?.per_page) query.set('per_page', String(params.per_page));
        if (params?.search) query.set('search', params.search);

        return await pbRequest<CurrentReports>(`/companies/${companyId}/reports/current?${query.toString()}`, {
            failure: 'Failed to fetch reports',
        });
    },

    /**
     * Get report history
     */
    async getReports(companyId: number, params?: { page?: number; per_page?: number; report_id?: number | null }): Promise<{ data: Report[]; meta: any }> {
        const query = new URLSearchParams();
        if (params?.page) query.set('page', String(params.page));
        if (params?.per_page) query.set('per_page', String(params.per_page));
        if (params?.report_id) query.set('report_id', String(params.report_id));

        const envelope = await pbRequestRaw<BaseAPIResponse<Report[]>>(
            `/companies/${companyId}/reports?${query.toString()}`,
            {
                failure: 'Failed to fetch reports',
            }
        );

        return { data: envelope.data || [], meta: envelope.meta || {} };
    },

    /**
     * Fetch exposure report history for a single employee (report_id=11 only).
     * Returns Hive-business-generated and per-person scheduled exposure reports
     * relevant to this employee, newest first.
     */
    async getEmployeeReports(companyId: number, employeeId: number, params?: { page?: number; per_page?: number }): Promise<{ data: Report[]; meta: any }> {
        const query = new URLSearchParams();
        if (params?.page) query.set('page', String(params.page));
        if (params?.per_page) query.set('per_page', String(params.per_page));

        const envelope = await pbRequestRaw<BaseAPIResponse<Report[]>>(
            `/companies/${companyId}/employees/${employeeId}/reports?${query.toString()}`,
            {
                failure: 'Failed to fetch employee reports',
            }
        );

        return { data: envelope.data || [], meta: envelope.meta || {} };
    },

    /**
     * Get a temporary download URL for a company report.
     *
     * `disposition: 'attachment'` asks S3 to answer with a Content-Disposition
     * attachment header, so a plain link saves the file. Leave it off for Open,
     * which wants the report rendered inline in a tab.
     */
    async getReportUrl(
        companyId: number,
        reportId: string,
        disposition?: 'attachment'
    ): Promise<string> {
        const query = disposition ? `?disposition=${disposition}` : '';

        const link = await pbRequestRaw<{ url: string; expires_in: number }>(
            `/companies/${companyId}/reports/${reportId}/url${query}`,
            { failure: 'No URL returned from server' }
        );

        if (!link.url || link.url.trim() === '') {
            throw new ApiError('No URL returned from server', 404);
        }

        return link.url;
    },
};
